import type { ReactNode } from 'react';

import {
  type LinkProps as RouterLinkProps,
  Link as RouterLink,
} from 'react-router-dom';

import { Button, type ButtonProps } from '../../components/button';
import { Link, type StrippedLinkProps } from '../../components/link';
import { Stack } from '../../components/stack';
import { Typography } from '../../components/typography';

export interface ErrorActionLink
  extends Omit<
      StrippedLinkProps,
      'children' | 'component' | 'href' | 'underline'
    >,
    Pick<
      RouterLinkProps,
      | 'reloadDocument'
      | 'replace'
      | 'state'
      | 'preventScrollReset'
      | 'relative'
      | 'to'
    > {
  label: string;
}

export type ErrorActionButton = Omit<
  ButtonProps<'button'>,
  'children' | 'variant' | 'color' | 'size' | 'component'
> & {
  label: string;
};

export interface ErrorActionButtonLink
  extends Omit<
      ButtonProps<'a'>,
      'children' | 'variant' | 'color' | 'size' | 'href' | 'component'
    >,
    Omit<RouterLinkProps, 'color'> {
  label: string;
}

export interface ErrorBaseProps {
  actionButton?: ErrorActionButton | ErrorActionButtonLink;
  actionLink?: ErrorActionLink;
  body: string[];
  icon: ReactNode;
  /**
   * Use the ErrorMessage component to include your error messages.
   * Wrap in fragment if more than one is included.
   */
  errorMessages?: ReactNode;
  title: string;
}

const isButtonLink = (
  item: ErrorActionButton | ErrorActionButtonLink,
): item is ErrorActionButtonLink => 'to' in item;

const ButtonLink = (props: ErrorActionButton | ErrorActionButtonLink) => {
  if (isButtonLink(props)) {
    return (
      <Button
        component={RouterLink}
        size="large"
        variant="contained"
        {...props}
      >
        {props.label}
      </Button>
    );
  }

  return (
    <Button size="large" variant="contained" {...props}>
      {props.label}
    </Button>
  );
};

// This component is used to handle application-crash level errors in some use cases, therefore localization cannot be included within this base component.
export const ErrorBase = ({
  actionButton,
  actionLink,
  errorMessages,
  title,
  body,
  icon,
}: ErrorBaseProps) => {
  return (
    <Stack
      direction="column"
      spacing={5}
      sx={(theme) => ({
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        margin: '0 auto',
        maxWidth: theme.maxWidth.paragraph,
        width: '100%',
      })}
    >
      <Stack spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
        {icon}
        <Typography
          variant="h1"
          component="h3"
          sx={(theme) => ({ color: theme.palette.secondary.dark })}
        >
          {title}
        </Typography>
        {body.map((text, index) => (
          <Typography
            key={`body-${index}`}
            color="textSecondary"
            variant="body2"
            sx={{
              whiteSpace: 'pre-line',
              textAlign: 'center',
            }}
          >
            {text}
          </Typography>
        ))}
        {errorMessages && (
          <Stack
            sx={{
              overflowX: 'scroll',
              width: '100%',
            }}
          >
            {errorMessages}
          </Stack>
        )}
      </Stack>

      {actionButton || actionLink ? (
        <Stack spacing={3} sx={{ alignItems: 'center' }}>
          {actionButton && <ButtonLink {...actionButton} />}
          {actionLink && (
            <Link component={RouterLink} {...actionLink}>
              {actionLink.label}
            </Link>
          )}
        </Stack>
      ) : null}
    </Stack>
  );
};
